import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMatch, useOutletContext } from 'react-router-dom'

import { Button } from '../../global/button/Button'
import { useSessionLanguage } from '../../global/context/SessionSettingsContext'
import { Locale } from '../../global/locale/Locale'
import { useHomePageLink } from '../../hooks/useHomePageUrl'
import { ScrollToIds, useScrollToElementIds } from '../../hooks/useScrollToElementIds'
import { ChartBarIcon } from '../../icons/ChartBarIcon'
import { ChartLineUpIcon } from '../../icons/ChartLineUpIcon'
import { ChatsCircleIcon } from '../../icons/ChatsCircleIcon'
import { DevicesIcon } from '../../icons/DevicesIcon'
import { DonutChartIcon } from '../../icons/DonutChartIcon'
import { SquaresFourIcon } from '../../icons/SquaresFourIcon'
import { Step1Icon } from '../../icons/Step1Icon'
import { Step2Icon } from '../../icons/Step2Icon'
import { Step3Icon } from '../../icons/Step3Icon'
import { Step4Icon } from '../../icons/Step4Icon'
import { PlatformInfoDto } from '../../model/PlatformInfoDto'
import { formatPlatformName, getPlatformTypes } from '../../model/PlatformTypeEnum'
import { Link } from '../../navigation/custom-react-router-dom'
import { Text, TextH2, TextH3, TextH4, TextLargeStrong } from '../../ui/Typography/Typography'
import { useApiClient } from '../../utils/ApiClient'
import { ClientApiClient } from '../../utils/clientApi'
import { useWindowResize } from '../../utils/domUtils'
import { useFetchOne } from '../../utils/useFetch'
import { useScrollToTop } from '../../utils/useScrollToTop'
import tradingviewRightDesktop from '../images/TradingView/tradingview-right-desktop.webp'
import tradingviewRightMobile from '../images/TradingView/tradingview-right-mobile.webp'
import { PlatformDownloadButton } from './PlatformIcon'
import { TradingViewBrokerReviewWidget } from './TradingViewBrokerReview'
import { usePlatformTradingViewDownloadLinksData } from './platformsTradingViewData'

import styles from './PlatformsTradingViewPage.module.scss'

interface TradingViewProps {
  platformInfo: PlatformInfoDto[]
}

export const PlatformsTradingViewPage: React.FC = () => {
  useScrollToTop()

  const { platformInfo = [] } = useOutletContext<TradingViewProps>()

  const isMobile = useWindowResize()
  const { scrollIntoView } = useScrollToElementIds()
  const locale = useSessionLanguage()

  const match = useMatch(`/${locale}/dashboard/tools/platforms/:platformType`)
  const platformType = match?.params?.platformType || ''

  useEffect(() => {
    const platformNames = getPlatformTypes().map((x) => x.shortName)
    if (platformNames.some((name) => platformType.includes(formatPlatformName(name)))) {
      scrollIntoView([ScrollToIds.ToolsPlatformsHeader])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformType])

  return isMobile ? (
    <TradingViewMobile platformInfo={platformInfo} />
  ) : (
    <TradingViewDesktop platformInfo={platformInfo} />
  )
}

const TradingViewMobile: React.FC<TradingViewProps> = (props) => {
  const { platformInfo } = props

  const { t } = useTranslation()
  const homePageUrl = useHomePageLink()

  const { getLocalizedVideoUrl } = useLocalizedVideos()

  return (
    <>
      <div className={styles.downloadSection}>
        <img src={tradingviewRightMobile} className={styles.hasMarginBottom} alt='' />
        <TextH2 isParagraph className={styles.hasMarginBottom}>
          {t('ToolsPlatforms.Unleash your trading skills with TradingView')}
        </TextH2>
        <Text className={styles.textSecondary} isParagraph>
          {t('ToolsPlatforms.TradingViewInfo.1')} {t('ToolsPlatforms.TradingViewInfo.2')}{' '}
          {t('ToolsPlatforms.TradingViewInfo.3')}
        </Text>
        <div className={styles.linksSectionAbout}>
          <div className={styles.linksSection}>
            <DownloadLinks platformInfo={platformInfo} />
          </div>
        </div>
      </div>

      <div className='p-4'>
        <div className={styles.analysisHeader}>
          <DonutChartIcon />
          <TextH4>{t('ToolsPlatforms.Advanced visual analysis')}</TextH4>
        </div>
        <Text className={styles.textSecondary}>
          {t(
            'ToolsPlatforms.Enhance an informed trading approach with over 110+ intelligent drawing tools'
          )}
        </Text>
      </div>

      <div className='p-4'>
        <div className={styles.analysisHeader}>
          <SquaresFourIcon />
          <TextH4>{t('ToolsPlatforms.Widest choice of indicators')}</TextH4>
        </div>
        <Text className={styles.textSecondary}>
          {t('ToolsPlatforms.The technical analysis brings limitless opportunities')}
        </Text>
      </div>

      <div className='p-4'>
        <div className={styles.analysisHeader}>
          <ChartBarIcon />
          <TextH4>{t('ToolsPlatforms.Unparalleled charting interface')}</TextH4>
        </div>
        <Text className={styles.textSecondary}>
          {t('ToolsPlatforms.Regardless of where you are in your trading path')}
        </Text>
      </div>

      <TextH2 isParagraph className={styles.hasMarginTop}>
        {t('ToolsPlatforms.Connect TradingView with your Tickmill Account')}
      </TextH2>
      <div className={styles.stepsSection}>
        <div className={styles.step}>
          <Step1Icon />
          <div>
            <TextLargeStrong isParagraph>{t('ToolsPlatforms.Set up accounts')}</TextLargeStrong>
            <Text className={styles.textSecondary}>
              {t(
                'ToolsPlatforms.Create or use an existing Tickmill TradingView account and make sure you have a TradingView profile'
              )}
            </Text>
          </div>
        </div>
        <div className={styles.step}>
          <Step2Icon />
          <div>
            <TextLargeStrong isParagraph>
              {t('ToolsPlatforms.Launch TradingView from Tickmill')}
            </TextLargeStrong>
            <Text className={styles.textSecondary}>
              {t('ToolsPlatforms.Head to the')}{' '}
              <Link to='/dashboard/traders-room/trading-accounts'>
                {t('Trading Account.Trading Accounts')}
              </Link>{' '}
              {t('ToolsPlatforms.section and click Trade')}
            </Text>
          </div>
        </div>
        <div className={styles.step}>
          <Step3Icon />
          <div>
            <TextLargeStrong isParagraph>
              {t('ToolsPlatforms.Log in with your Tickmill credentials')}
            </TextLargeStrong>
            <Text className={styles.textSecondary}>
              {t('ToolsPlatforms.Enter your TradingView account credentials when prompted.')}
            </Text>
          </div>
        </div>
        <div className={styles.step}>
          <Step4Icon />
          <div>
            <TextLargeStrong isParagraph>
              {t('ToolsPlatforms.You are ready to trade')}
            </TextLargeStrong>
            <Text className={styles.textSecondary}>
              {t(
                'ToolsPlatforms.You are now connected and ready to trade across all TradingView platforms'
              )}
            </Text>
          </div>
        </div>
      </div>
      <div className={styles.tradingviewVideo}>
        <video src={getLocalizedVideoUrl('tmt-21095-trading-view')} controls />
      </div>
      <TextH2>{t('ToolsPlatforms.TradingView platform features')}</TextH2>

      <div>
        <div className={styles.featureHeader}>
          <DevicesIcon />
          <TextH3>{t('ToolsPlatforms.Multi-platform experience')}</TextH3>
        </div>
        <Text className={styles.textSecondary}>
          {t('ToolsPlatforms.Take advantage of TradingViews powerful browser')}
        </Text>
      </div>

      <div>
        <div className={styles.featureHeader}>
          <ChartLineUpIcon />
          <TextH3>{t('ToolsPlatforms.Charts that move markets')}</TextH3>
        </div>
        <Text className={styles.textSecondary}>
          {t('ToolsPlatforms.Whether youd like to simply look up the latest stock price')}
        </Text>
      </div>

      <div>
        <div className={styles.featureHeader}>
          <ChatsCircleIcon />
          <TextH3>{t('ToolsPlatforms.TradingView community')}</TextH3>
        </div>
        <Text className={styles.textSecondary}>
          {t('ToolsPlatforms.Get inspiration for your next trade. Dont reinvent the wheel')}
        </Text>
      </div>

      <div className={styles.buttonSection}>
        <Link className={styles.learnMoreLink} to={`${homePageUrl}/trading-platforms/tradingview`}>
          <Button appearance='primary' size='M' type='button' fullWidth className={styles.button}>
            {t('Learn More')}
          </Button>
        </Link>
      </div>

      <div className={styles.videoGuidesSection}>
        <div className={styles.guideTitle}>
          <TextH2 className={styles.videoGuidesTitle}>
            {t('ToolsPlatforms.New to TradingView?')}
          </TextH2>
          <Text className={styles.textSecondary}>
            {t('ToolsPlatforms.Below you will find video guides to get you started.')}
          </Text>
        </div>
        <div className={styles.videoGuidesList}>
          <div className={styles.videoGuideItem}>
            <TextH3>{t('ToolsPlatforms.How to Set Up a Chart on TradingView')}</TextH3>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-set-up-a-chart-on-tradingview.mp4'
              controls
            />
          </div>

          <div className={styles.videoGuideItem}>
            <TextH3>{t('ToolsPlatforms.How to Use Alerts on TradingView')}</TextH3>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-alerts-on-tradingview.mp4'
              controls
            />
          </div>

          <div className={styles.videoGuideItem}>
            <TextH3>{t('ToolsPlatforms.How to Use Drawing Tools on TradingView')}</TextH3>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-drawing-tools-on-tradingview.mp4'
              controls
            />
          </div>

          <div className={styles.videoGuideItem}>
            <TextH3>{t('ToolsPlatforms.How to Use Indicators on TradingView')}</TextH3>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-indicators-on-tradingview.mov'
              controls
            />
          </div>
        </div>
      </div>
      {/*<TradingViewBrokerReviewWidget />*/}
      {/*<TradingViewBrokerReviewWidget isGrid />*/}
    </>
  )
}

const TradingViewDesktop: React.FC<TradingViewProps> = (props) => {
  const { platformInfo } = props

  const { t } = useTranslation()

  const homePageUrl = useHomePageLink()
  const { getLocalizedVideoUrl } = useLocalizedVideos()

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.downloadSection}>
          <div className={styles.textSection}>
            <TextH2 isParagraph>
              {t('ToolsPlatforms.Unleash your trading skills with TradingView')}
            </TextH2>
            <Text className={styles.textSecondary}>{t('ToolsPlatforms.TradingViewInfo.1')}</Text>
            <br />
            <Text className={styles.textSecondary}>{t('ToolsPlatforms.TradingViewInfo.2')}</Text>
            <br />
            <Text className={styles.textSecondary}>{t('ToolsPlatforms.TradingViewInfo.3')}</Text>
            <div className={styles.linksSection}>
              <DownloadLinks platformInfo={platformInfo} />
            </div>
          </div>
          <div>
            <img src={tradingviewRightDesktop} alt='' className={styles.rightSection} />
          </div>
        </div>
      </div>

      <div className={styles.analysisSection}>
        <div className={styles.item}>
          <div className={styles.analysisHeader}>
            <DonutChartIcon />
            <TextH4>{t('ToolsPlatforms.Advanced visual analysis')}</TextH4>
          </div>
          <Text className={styles.textSecondary}>
            {t(
              'ToolsPlatforms.Enhance an informed trading approach with over 110+ intelligent drawing tools'
            )}
          </Text>
        </div>
        <div className={styles.item}>
          <div className={styles.analysisHeader}>
            <SquaresFourIcon />
            <TextH4>{t('ToolsPlatforms.Widest choice of indicators')}</TextH4>
          </div>
          <Text className={styles.textSecondary}>
            {t('ToolsPlatforms.The technical analysis brings limitless opportunities')}
          </Text>
        </div>
        <div className={styles.item}>
          <div className={styles.analysisHeader}>
            <ChartBarIcon />
            <TextH4>{t('ToolsPlatforms.Unparalleled charting interface')}</TextH4>
          </div>
          <Text className={styles.textSecondary}>
            {t('ToolsPlatforms.Regardless of where you are in your trading path')}
          </Text>
        </div>
      </div>

      <div className={styles.connectSection}>
        <div className={styles.chartContainer}>
          <video src={getLocalizedVideoUrl('tmt-21095-trading-view')} controls />
        </div>
        <div className={styles.stepsContainer}>
          <div className={styles.stepsHeader}>
            <TextH2>{t('ToolsPlatforms.Connect TradingView with your Tickmill Account')}</TextH2>
          </div>
          <div className={styles.step}>
            <Step1Icon />
            <div>
              <TextLargeStrong isParagraph>{t('ToolsPlatforms.Set up accounts')}</TextLargeStrong>
              <Text className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Create or use an existing Tickmill TradingView account and make sure you have a TradingView profile'
                )}
              </Text>
            </div>
          </div>
          <div className={styles.step}>
            <Step2Icon />
            <div>
              <TextLargeStrong isParagraph>
                {t('ToolsPlatforms.Launch TradingView from Tickmill')}
              </TextLargeStrong>
              <Text className={styles.textSecondary}>
                {t('ToolsPlatforms.Head to the')}{' '}
                <Link to='/dashboard/traders-room/trading-accounts'>
                  {t('Trading Account.Trading Accounts')}
                </Link>{' '}
                {t('ToolsPlatforms.section and click Trade')}
              </Text>
            </div>
          </div>
          <div className={styles.step}>
            <Step3Icon />
            <div>
              <TextLargeStrong isParagraph>
                {t('ToolsPlatforms.Log in with your Tickmill credentials')}
              </TextLargeStrong>
              <Text className={styles.textSecondary}>
                {t('ToolsPlatforms.Enter your TradingView account credentials when prompted.')}
              </Text>
            </div>
          </div>
          <div className={styles.step}>
            <Step4Icon />
            <div>
              <TextLargeStrong isParagraph>
                {t('ToolsPlatforms.You are ready to trade')}
              </TextLargeStrong>
              <Text className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.You are now connected and ready to trade across all TradingView platforms'
                )}
              </Text>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.title}>
        <TextH2>{t('ToolsPlatforms.TradingView platform features')}</TextH2>
      </div>

      <div className={styles.featuresSection}>
        <div className={styles.item}>
          <div className={styles.featureHeader}>
            <DevicesIcon />
            <TextH3>{t('ToolsPlatforms.Multi-platform experience')}</TextH3>
          </div>
          <Text className={styles.textSecondary}>
            {t('ToolsPlatforms.Take advantage of TradingViews powerful browser')}
          </Text>
        </div>
        <div className={styles.item}>
          <div className={styles.featureHeader}>
            <ChartLineUpIcon />
            <TextH3>{t('ToolsPlatforms.Charts that move markets')}</TextH3>
          </div>
          <Text className={styles.textSecondary}>
            {t('ToolsPlatforms.Whether youd like to simply look up the latest stock price')}
          </Text>
        </div>
        <div className={styles.item}>
          <div className={styles.featureHeader}>
            <ChatsCircleIcon />
            <TextH3>{t('ToolsPlatforms.TradingView community')}</TextH3>
          </div>
          <Text className={styles.textSecondary}>
            {t('ToolsPlatforms.Get inspiration for your next trade. Dont reinvent the wheel')}
          </Text>
        </div>
      </div>

      <div className={styles.buttonSection}>
        <Link target='_blank' to={`${homePageUrl}/trading-platforms/tradingview`}>
          <Button appearance='primary' size='L' type='button' className={styles.button}>
            {t('Learn More')}
          </Button>
        </Link>
      </div>

      <div className={styles.videoGuidesSection}>
        <div className={styles.guideTitle}>
          <TextH2 className={styles.videoGuidesTitle}>
            {t('ToolsPlatforms.New to TradingView?')}
          </TextH2>
          <Text className={styles.textSecondary}>
            {t('ToolsPlatforms.Below you will find video guides to get you started.')}
          </Text>
        </div>
        <div className={styles.videoGuidesList}>
          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-set-up-a-chart-on-tradingview.mp4'
              controls
            />
            <div className={styles.content}>
              <TextH3>{t('ToolsPlatforms.How to Set Up a Chart on TradingView')}</TextH3>
              <Text className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Quickly learn how to adjust chart types, timeframes, and themes to match your trading style.'
                )}
              </Text>
            </div>
          </div>

          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-alerts-on-tradingview.mp4'
              controls
            />
            <div className={styles.content}>
              <TextH3>{t('ToolsPlatforms.How to Use Alerts on TradingView')}</TextH3>
              <Text className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Set price and indicator alerts so you never miss a key market move.'
                )}
              </Text>
            </div>
          </div>

          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-drawing-tools-on-tradingview.mp4'
              controls
            />
            <div className={styles.content}>
              <TextH3>{t('ToolsPlatforms.How to Use Drawing Tools on TradingView')}</TextH3>
              <Text className={styles.textSecondary}>
                {t(
                  'ToolsPlatforms.Master trend lines, shapes, and tools to boost your technical analysis.'
                )}
              </Text>
            </div>
          </div>

          <div className={styles.videoGuideItem}>
            <video
              src='https://d1fyjtrsl71uh.cloudfront.net/videos/video-tutorials/trading-view-videos/how-to-use-indicators-on-tradingview.mov'
              controls
            />
            <div className={styles.content}>
              <TextH3>{t('ToolsPlatforms.How to Use Indicators on TradingView')}</TextH3>
              <Text className={styles.textSecondary}>
                {t('ToolsPlatforms.Learn to add and tweak indicators for sharper trade decisions.')}
              </Text>
            </div>
          </div>
        </div>
      </div>
      {/*<TradingViewBrokerReviewWidget />*/}
      {/*<TradingViewBrokerReviewWidget isGrid />*/}
    </div>
  )
}

const DownloadLinks: React.FC<TradingViewProps> = (props) => {
  const { platformDownloadLinksData } = usePlatformTradingViewDownloadLinksData(props.platformInfo)

  return (
    <>
      {platformDownloadLinksData.map((x) => (
        <PlatformDownloadButton className={styles.hasMarginRight} to={x.link} key={x.link}>
          {x.icon}
        </PlatformDownloadButton>
      ))}
    </>
  )
}

export interface VideoData {
  url: string
  id: string
  category: string
  languageId: Locale
}

export const useLocalizedVideos = (): {
  getLocalizedVideoUrl: (fileName: string) => string
  loading: boolean
} => {
  const locale = useSessionLanguage()

  const apiClient = useApiClient(ClientApiClient)

  const localizeVideoCallback = useCallback(() => {
    return apiClient.getLocalizedVideos(locale === 'tr' ? 'en' : locale)
  }, [apiClient, locale])

  const { data: videos = [], isLoading: loading } = useFetchOne(localizeVideoCallback)

  const getLocalizedVideoUrl = (fileName: string): string => {
    if (loading || videos.length === 0) {
      return fileName
    }

    const matchingVideo = videos.find((v) => v.url.includes(fileName))

    return matchingVideo?.url || fileName
  }

  return { getLocalizedVideoUrl, loading }
}
